import { HttpParams } from '@angular/common/http';
import { Component, Injector, OnInit } from '@angular/core';
import { Params } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  includes,
  isEmpty,
  isUndefined,
  toUpper,
  uniqBy,
  upperCase,
} from 'lodash';
import { ListingBaseComponent } from 'src/app/shared/components/base/listing-base.component';
import { DATE_RANGE_OPTIONS } from 'src/app/shared/components/purple-date-picker/purple-date-picker.constant';
import {
  DEFAULT_PAGE_INDEX,
  FILE_TYPE,
  NUMBER_RECORDS_PER_PAGE,
} from 'src/app/shared/constants/constant-list';
import { ApiResponseBodyInterface } from 'src/app/shared/interfaces/api-response.interface';
import { AppPartnersService } from 'src/app/shared/services/app-partners.service';
import {
  selectAllPartnersTransactions,
  selectTransactionsLoading,
} from 'src/app/shared/stores/slices/AppPartners';
import { DATE_FILTERS } from '../user-comparison-dashboard/contants';
import moment from 'moment';
import { LRC_TRANSACTIONS } from 'src/app/shared/constants/apis-list';
import * as CONSTANT from 'src/app/shared/constants/constant-list';

@Component({
  selector: 'app-lrc-partners',
  templateUrl: './lrc-partners.component.html',
  styleUrls: ['./lrc-partners.component.scss'],
})
export class LrcPartnersComponent
  extends ListingBaseComponent
  implements OnInit
{
  tableDataListItems: any[] = [];
  tableFilters: any[] = [];

  params: HttpParams = new HttpParams();
  queryParams: Params = {};

  filterValue: string = 'last_30_days';
  filterLabel?: string;
  dateFilters: any[] = [];
  fromDate: Date | null = null;
  toDate: Date | null = null;

  DATE_FILTERS: any[] = DATE_FILTERS;

  page: number = DEFAULT_PAGE_INDEX;
  pageLimit: number = NUMBER_RECORDS_PER_PAGE;
  totalNumberOfRecords = 0;
  totalNumberOfPages = 0;
  lastPage = 0;
  hasMore = false;
  loading = false;

  searchValue: string = '';

  PENDING = [
    CONSTANT.CASHOUT_TRANSACTION_STATUS.CASHING_OUT,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.CREATED,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.ON_HOLD,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.PENDING,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.RECEIVED,
  ];

  RETURNED = [
    CONSTANT.CASHOUT_TRANSACTION_STATUS.CANCELLED,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.EXPIRED,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.FAILED,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.REJECTED,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.RETURNED,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.REVERSED,
  ];

  COMPLETED = [
    CONSTANT.CASHOUT_TRANSACTION_STATUS.ACCEPTED,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.CASHED_OUT,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.COMPLETED,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.CREDITED,
    CONSTANT.CASHOUT_TRANSACTION_STATUS.PROCESSED,
  ];

  private transactionStatusMapping(status: string) {
    if (includes(this.PENDING, toUpper(status))) {
      return 'PENDING';
    } else if (includes(this.RETURNED, toUpper(status))) {
      return 'RETURNED';
    } else if (includes(this.COMPLETED, toUpper(status))) {
      return 'COMPLETED';
    }
  }

  constructor(
    injector: Injector,
    private partnerService: AppPartnersService,
    private store: Store
  ) {
    super(injector);

    this.params = this.params.set('limit', NUMBER_RECORDS_PER_PAGE);
    this.params = this.params.set('offset', DEFAULT_PAGE_INDEX);

    this.store
      .select(selectTransactionsLoading)
      .subscribe((resp) => (this.loading = resp));

    this.store
      .select(selectAllPartnersTransactions)
      .subscribe((resource: ApiResponseBodyInterface | null | undefined) => {
        if (resource?.results && Array.isArray(resource.results)) {
          this.tableDataListItems = [];

          this.tableDataListItems = resource?.results?.map((item) => ({
            ...item,
            loading: false,
            ref:
              item?.type === 'CASH_OUT'
                ? item?.transaction_object?.purpl_reference
                : item?.transaction_object?.partner_reference,
            status: this.transactionStatusMapping(item.status),
            wallet_type: upperCase(item?.wallet_type?.name),
            amount: item?.currency + ' ' + Number(item?.amount),
            sender_receiver_relationship:
              item?.transaction_object?.sender_receiver_relationship,
            purpose: item?.transaction_object?.purpose,
            amount1: Number(item?.amount),
            location: item?.transaction_object?.receiver?.country,
            address: item?.transaction_object?.receiver?.city,
          }));

          const totalPages = Math.ceil(
            Number(resource?.count) / Number(this.pageLimit)
          );
          this.lastPage = totalPages;
          this.totalNumberOfPages = totalPages;
          this.totalNumberOfRecords = Number(resource?.count);
          this.hasMore = 0 <= totalPages - this.page;
        }
      });
  }

  ngOnInit(): void {
    const options: any = DATE_RANGE_OPTIONS.find(
      (item) => item.value === this.filterValue
    );

    this.filterLabel = options?.label;
    this.dateFilters = options?.filters;

    if (!isEmpty(String(options.startDate))) {
      this.fromDate = moment(options.startDate).toDate();
    }
    if (!isEmpty(String(options.endDate))) {
      this.toDate = moment(options.endDate).toDate();
    }

    this.initData();
  }

  pending() {
    this.params = this.params.set('transaction_status', this.PENDING.join(','));
  }

  returned() {
    this.params = this.params.set(
      'transaction_status',
      this.RETURNED.join(',')
    );
  }

  completed() {
    this.params = this.params.set(
      'transaction_status',
      this.COMPLETED.join(',')
    );
  }

  private initData() {
    const OFFSET = this.pageLimit * (this.page - 1);

    if (this.tableFilters && this.tableFilters.length) {
      this.tableFilters = uniqBy(this.tableFilters, 'field');

      this.tableFilters.forEach((item, index) => {
        Object.entries(item).forEach(([key, value]) => {
          if (typeof value !== 'undefined') {
            if (item.field === 'transaction_status') {
              if (item.search === 'PENDING') {
                this.pending();
              } else if (item.search === 'RETURNED') {
                this.returned();
              } else if (item.search === 'COMPLETED') {
                this.completed();
              } else {
                this.params = this.params.delete('transaction_status');
              }
            } else {
              this.params = this.params.set(item.field, String(value));

              this.params =
                value === 'all'
                  ? this.params.delete(item.field, 'all')
                  : this.params.set(item.field, item.search);
            }

            this.params = this.params.set(
              'offset',
              this.page > 1 ? OFFSET : ''
            );
          }
        });
      });
    } else {
      for (let index = 0; index < 4; index++) {
        this.params = this.params.delete(`fields[${index}].field`);
        this.params = this.params.delete(`fields[${index}].search`);
      }
    }

    this.params = this.params.set(
      'search',
      !isEmpty(this.searchValue) ? this.searchValue : ''
    );

    this.params = this.params.set('limit', Number(this.pageLimit));
    this.params = this.params.set('offset', this.page > 1 ? OFFSET : '');

    this.partnerService.getAllLRCTransactions(this.params);
  }

  onPageChange(ev: number) {
    this.page = Number(ev);

    this.initData();
  }

  onPageLimitChange(event: number) {
    if (!isUndefined(event)) {
      this.pageLimit = Number(event);
      this.page = DEFAULT_PAGE_INDEX;

      this.initData();
    }
  }

  onColumnFilterChange(event: any) {
    this.tableFilters = event;
    this.page = DEFAULT_PAGE_INDEX;

    this.initData();
  }

  searchInput(ev: string) {
    this.searchValue = ev;

    this.page = 1;
    this.initData();
  }

  onDateSelection(event: any) {
    const options: any = DATE_RANGE_OPTIONS.find(
      (item) => item.value === event.filter
    );
    this.filterValue = options?.value;
    this.filterLabel = options?.label;
    this.fromDate = event.startDateJS;
    this.toDate = event.endDateJS;
    this.dateFilters = options.filters;

    this.params = this.params.set(
      'datetime_created__gte',
      this.fromDate?.toISOString()!
    );
    this.params = this.params.set(
      'datetime_created__lte',
      this.toDate?.toISOString()!
    );

    this.initData();
  }

  async onExportUsers() {
    // if (
    //   !this.checkAccessModule(
    //     this.FEATURES.COMPLIANCE.name,
    //     this.PERMISSION.EXPORT
    //   )
    // ) {
    //   this.toastrService.warning(
    //     'You do not have enough privilege to access this functionality',
    //     'Access Denied'
    //   );
    //   return;
    // }

    this.dataService.exportFile(
      'GET',
      LRC_TRANSACTIONS,
      FILE_TYPE.CSV_TEXT,
      'TRANSACTIONS.csv'
    );
  }
}
